import { useCallback } from 'react';
import { useAppSelector } from '../redux/store/hooks';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Billing history limit for roles without the full-history permission.
 *
 * `admin` and `super_admin` may browse unlimited history on the Billing,
 * Top Sheet and Cashbook screens. Every other role (including users with
 * custom roles) is limited to the current day plus the six preceding days
 * unless its custom role holds {@link FULL_BILLING_HISTORY_PERMISSION}.
 * The API enforces the same window server-side.
 */
export const BILLING_HISTORY_DAYS = 7;

/**
 * Permission that lifts the {@link BILLING_HISTORY_DAYS} window. It is an
 * in-page capability: the role still needs the screen's own permission to open
 * it, and this code only widens the date range it may query.
 */
export const FULL_BILLING_HISTORY_PERMISSION = 'billing:history:full';

export interface BillingPeriod {
  from: string;
  to: string;
}

/** Formats a Date as a local `YYYY-MM-DD` (never shifts the day via UTC). */
function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Whether the supplied roles may see the complete billing history. */
export function canViewFullBillingHistory(
  roles?: string[] | null,
  permissions?: Iterable<string> | null,
): boolean {
  const list = roles || [];
  if (list.includes('admin') || list.includes('super_admin')) return true;

  if (!permissions) return false;
  const granted = permissions instanceof Set ? permissions : new Set(permissions);
  return matchesPermission(FULL_BILLING_HISTORY_PERMISSION, granted);
}

/**
 * Segment-wise wildcard match, mirroring the backend `PermissionsGuard` and
 * `usePermissions().hasPermission`: a granted `billing:history:*` satisfies
 * `billing:history:full`.
 */
function matchesPermission(required: string, granted: Set<string>): boolean {
  if (granted.has(required)) return true;

  const requiredParts = required.split(':');
  for (const code of granted) {
    const parts = code.split(':');
    if (parts.length !== requiredParts.length) continue;
    const matches = parts.every(
      (part, index) =>
        part === '*' || requiredParts[index] === '*' || part === requiredParts[index],
    );
    if (matches) return true;
  }
  return false;
}

/** Earliest day (inclusive) a restricted role may query. */
export function billingHistoryFloor(today: Date = new Date()): string {
  return toDateKey(
    new Date(today.getFullYear(), today.getMonth(), today.getDate() - (BILLING_HISTORY_DAYS - 1)),
  );
}

/** Latest day (inclusive) a restricted role may query: today. */
export function billingHistoryCeiling(today: Date = new Date()): string {
  return toDateKey(today);
}

/**
 * Narrows a period to the allowed seven-day window. Empty boundaries mean
 * "unbounded" and are replaced by the window edges rather than left open.
 */
export function clampBillingPeriod(period: BillingPeriod, today: Date = new Date()): BillingPeriod {
  const floor = billingHistoryFloor(today);
  const ceiling = billingHistoryCeiling(today);

  let from = period.from ? period.from.slice(0, 10) : floor;
  let to = period.to ? period.to.slice(0, 10) : ceiling;

  if (from < floor) from = floor;
  if (to > ceiling) to = ceiling;

  // An inverted window (e.g. a stale "from" after today) collapses to the
  // default seven-day window instead of returning nothing.
  if (from > to) {
    from = floor;
    to = ceiling;
  }

  return { from, to };
}

/** Role-aware billing history access for financial screens. */
export function useBillingHistoryAccess() {
  const roles = useAppSelector((state) => state.auth.user?.roles);
  const { userPermissions } = usePermissions();
  const unrestricted = canViewFullBillingHistory(roles, userPermissions);

  const clamp = useCallback(
    (period: BillingPeriod) => (unrestricted ? period : clampBillingPeriod(period)),
    [unrestricted],
  );

  return {
    unrestricted,
    /** Minimum selectable date for restricted roles. */
    minDate: unrestricted ? '' : billingHistoryFloor(),
    /** Maximum selectable date for restricted roles. */
    maxDate: unrestricted ? '' : billingHistoryCeiling(),
    /** Clamp helper bound to the current user's permissions. */
    clamp,
  };
}
